import { format } from 'date-fns';
import type { OrderCounts, OrderPackage, OrderSummary, OverviewReceiving, WorkCounts } from '@/api/orders';
import { isOpenProblem, isResolvedProblem } from '@/api/packages';
import type { StatusTone } from '@/lib/status';

// ---------------------------------------------------------------------------
// Status → lifecycle stage. The page is organised around the journey, not the
// raw status enum: what's still on order, what has arrived but isn't balanced,
// and what's finished.
// ---------------------------------------------------------------------------

export type Stage = 'to_order' | 'on_order' | 'in_transit' | 'arrived' | 'done' | 'cancelled';

const STAGE_BY_STATUS: Record<string, Stage> = {
  announced: 'to_order',
  placed: 'on_order',
  confirmed: 'on_order',
  invoiced: 'on_order',
  partially_shipped: 'in_transit',
  shipped: 'in_transit',
  partially_received: 'arrived',
  received: 'arrived',
  reconciled: 'done',
  closed: 'done',
  cancelled: 'cancelled',
  wont_order: 'cancelled',
};

export function stageOf(order: OrderSummary): Stage {
  return STAGE_BY_STATUS[order.status] || 'on_order';
}

export const STAGE_ORDER: Stage[] = ['to_order', 'on_order', 'in_transit', 'arrived', 'done', 'cancelled'];

export const STAGE_TITLES: Record<Stage, string> = {
  to_order: 'To order',
  on_order: 'On order',
  in_transit: 'On the way',
  // "Arrived", not "Received" — the stage spans partially_received AND
  // received, and reusing the status word made one label mean two things.
  arrived: 'Arrived',
  done: 'Done',
  cancelled: 'Cancelled',
};

export const STATUS_LABELS: Record<string, string> = {
  announced: 'To be ordered',
  placed: 'Placed',
  confirmed: 'Confirmed',
  invoiced: 'Invoiced',
  partially_shipped: 'Partially shipped',
  shipped: 'Shipped',
  partially_received: 'Partially received',
  received: 'Received',
  reconciled: 'Reconciled',
  closed: 'Closed',
  cancelled: 'Cancelled',
  wont_order: "Won't order",
};

/**
 * The needs-setup breakdown, phrased ONCE. Four surfaces used to write their
 * own copy of these three facts (row hint, To-order hint, Set up badge, the
 * journey rail's warnings) and the wording drifted between them; every
 * consumer now renders these entries and adds only its own framing.
 */
export function setupBreakdown(counts: OrderCounts): Array<{
  key: 'new_products' | 'unresolved' | 'releases';
  text: string;
}> {
  const n = counts.newProductLines;
  const u = counts.unresolvedLines;
  const r = counts.releaseNamesMissing;
  return [
    n > 0 ? {
      key: 'new_products' as const,
      text: `${n} product${n === 1 ? '' : 's'} confirmed not in the catalog`,
    } : null,
    u > 0 ? {
      key: 'unresolved' as const,
      text: `${u} product${u === 1 ? '' : 's'} not resolved against the catalog yet`,
    } : null,
    r > 0 ? {
      key: 'releases' as const,
      text: `${r} release${r === 1 ? '' : 's'} checked against the catalog and not found`,
    } : null,
  ].filter((x): x is NonNullable<typeof x> => x != null);
}

/**
 * The count-difference vocabulary, shared by the Decide table and the balance
 * report (each used to carry its own copy).
 */
export const DISCREPANCY_KIND_LABELS: Record<string, string> = {
  short: 'Short',
  over: 'Over',
  not_received: 'Not received',
  not_invoiced: 'Not invoiced',
};


/**
 * The ordering queue's sort: window closing soonest first (no deadline sinks),
 * newest journey breaking ties. Shared by the To-order view and the stage
 * group on All orders so the two lists can never order differently.
 */
export function toOrderCompare(a: OrderSummary, b: OrderSummary): number {
  return (a.orderDueDate || '9999').localeCompare(b.orderDueDate || '9999')
    || Number(b.id) - Number(a.id);
}


// ---------------------------------------------------------------------------
// Package tracking rollup — many EasyPost trackers, one chip on the order row.
// ---------------------------------------------------------------------------

export const PKG_STATUS_LABELS: Record<string, string> = {
  unknown: 'Status unknown',
  pre_transit: 'Label created',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  available_for_pickup: 'Ready for pickup',
  delivered: 'Delivered',
  return_to_sender: 'Returning to sender',
  failure: 'Delivery problem',
  cancelled: 'Cancelled',
  error: 'Tracking error',
};

export function pkgStatusLabel(status: string): string {
  return PKG_STATUS_LABELS[status] || status;
}

export interface PackagesRollup {
  /** Winning precedence bucket: worst news first, "delivered" only when it's ALL the news. */
  kind: 'problem' | 'arriving' | 'moving' | 'pending' | 'delivered';
  label: string;
  /** Soonest ETA among undelivered packages; the latest delivery date once all landed. */
  date: string | null;
  tone: StatusTone;
  total: number;
  delivered: number;
}

/**
 * One general status for a set of trackers. Precedence is by attention needed,
 * not chronology: any problem outranks everything, an arriving truck outranks
 * the ones still crossing the country, and "delivered" is only the headline
 * when every package has landed.
 */
export function rollupPackages(all: OrderPackage[]): PackagesRollup | null {
  // A settled problem is neither pending nor news; the rest of the set speaks.
  const packages = all.filter((p) => !isResolvedProblem(p));
  if (packages.length === 0) return null;
  const any = (...statuses: string[]) => packages.some((p) => statuses.includes(p.status));
  const deliveredCount = packages.filter((p) => p.status === 'delivered').length;
  const base = { total: packages.length, delivered: deliveredCount };

  const etas = packages
    .filter((p) => p.status !== 'delivered')
    .map((p) => p.estDeliveryDate)
    .filter((d): d is string => d != null)
    .sort();
  const eta = etas[0] ?? null;

  if (packages.some(isOpenProblem)) {
    return { ...base, kind: 'problem', label: 'Delivery problem', date: eta,
      tone: 'danger' };
  }
  if (any('out_for_delivery', 'available_for_pickup')) {
    return { ...base, kind: 'arriving',
      label: any('out_for_delivery') ? 'Out for delivery' : 'Ready for pickup', date: eta,
      // Close, but still the carrier's job: moving, not needing you.
      tone: 'info' };
  }
  if (any('in_transit')) {
    return { ...base, kind: 'moving', label: 'In transit', date: eta,
      tone: 'info' };
  }
  if (deliveredCount === packages.length) {
    const landed = packages.map((p) => p.deliveredAt).filter((d): d is string => d != null).sort();
    return { ...base, kind: 'delivered', label: 'Delivered',
      date: landed[landed.length - 1] ?? null,
      tone: 'success' };
  }
  return { ...base, kind: 'pending',
    label: any('pre_transit') ? 'Label created' : 'Tracking pending', date: eta,
    tone: 'neutral' };
}

// ---------------------------------------------------------------------------
// The one date worth a column. A journey collects seven of them and only ever
// has one that matters: the next thing that has to happen, or — once nothing is
// pending — the last thing that did.
// ---------------------------------------------------------------------------

export interface Milestone {
  /** What the date IS, in one or two words. */
  label: string;
  date: string;
  /** The long form, for the tooltip. */
  hint: string;
}

/**
 * Precedence runs backwards along the journey — the furthest-along fact wins,
 * because that's the one that hasn't been superseded. A balanced receiving ends
 * the story; short of that the goods landing beats the truck's ETA, which beats
 * the vendor's guess, which beats the day we ordered.
 *
 * Deliberately NOT in here: production_completion_date. The column exists but
 * nothing has ever written to it (0 of 151 live orders), so a lane for it would
 * be permanently blank.
 */
export function orderMilestone(
  order: OrderSummary,
  receivings: OverviewReceiving[]
): Milestone | null {
  const stage = stageOf(order);
  const rollup = rollupPackages(order.packages);
  const latest = (dates: (string | null)[]) =>
    dates.filter((d): d is string => d != null).sort().at(-1) ?? null;

  const balanced = latest(receivings.map((r) => r.balancedAt));
  if (balanced && receivings.every((r) => r.balancedAt)) {
    return { label: 'balanced', date: balanced, hint: 'Balanced — costs applied and the math ties' };
  }

  const received = latest(receivings.map((r) => r.dateCreated));
  if (received) {
    return { label: 'received', date: received, hint: 'Received into the warehouse' };
  }

  if (rollup?.date) {
    if (rollup.kind === 'delivered') {
      return { label: 'delivered', date: rollup.date, hint: 'Carrier delivered the last package' };
    }
    return { label: 'ETA', date: rollup.date, hint: 'Soonest estimated delivery of any package' };
  }

  // Nothing has shipped yet, so the only live date is the window closing.
  if (stage === 'to_order' && order.orderDueDate) {
    return { label: 'order by', date: order.orderDueDate, hint: 'Deadline to place this order' };
  }
  if (order.expectedAvailability) {
    return { label: 'ships', date: order.expectedAvailability,
      hint: 'When the vendor says it ships or releases' };
  }
  if (order.placedDate) {
    return { label: 'ordered', date: order.placedDate, hint: 'Date the order was placed' };
  }
  return null;
}

/**
 * How urgent a date something must happen by is: `danger` once it has passed
 * (a lapsed window still needs a decision), `warning` inside the coming week
 * (today through six days out — the overview's "due this week"), `neutral`
 * beyond. Every deadline pill — order by, street date, ship date — reads this.
 */
export function dueTone(date: string | null | undefined): StatusTone | null {
  if (!date) return null;
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const days = Math.round((new Date(`${date.slice(0, 10)}T00:00:00`).getTime() - start) / 86_400_000);
  if (days < 0) return 'danger';
  if (days <= 6) return 'warning';
  return 'neutral';
}

/**
 * What the Orders tab badge counts: the queues that empty when worked —
 * ordering decisions, setup, settle/investigate/move/document/balance work,
 * and live delivery problems. Deliberately NOT prices (large and
 * discretionary — its own row shows the actionable count) or follow-up
 * watches — a badge that never clears is noise.
 */
export function workBadgeTotal(work: WorkCounts): number {
  return work.actions + work.toOrder + work.setup + work.decide
    + work.investigate + work.move
    + work.documents + work.balance + work.problems
    // Unattributed carrier bills are actionable now; receivings merely holding
    // open for a duty (awaitingDuty) are waiting, not work, so they stay out.
    + work.unattributedCarrierCharges;
}

// ---------------------------------------------------------------------------
// acot backend deep links — every work view ends at a real page, not a dead end.
// ---------------------------------------------------------------------------

/** The receiving-edit grid — where cost edits and line moves actually happen. */
export const acotReceivingUrl = (receivingId: number): string =>
  `https://backend.acherryontop.com/receiving/edit/${receivingId}`;

/** The PO editor in the backend. */
export const acotPoUrl = (poId: number): string =>
  `https://backend.acherryontop.com/po/edit/${poId}`;

/** Create-a-supplier page, for journeys flagged new_supplier_needed. */
export const ACOT_NEW_SUPPLIER_URL = 'https://backend.acherryontop.com/supplier/new';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function money(value: number | null | undefined, currency = 'USD'): string {
  if (value == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

/** Unit costs are 5-decimal in acot-db; show them without inventing precision. */
export function unitCost(value: number | null | undefined): string {
  if (value == null) return '—';
  const s = value.toFixed(5).replace(/0{1,3}$/, '');
  return s.endsWith('.') ? `${s}00` : s;
}

/** Date-only strings parse as UTC midnight; pin them to local time so they don't render a day early. */
export function parseLocal(value: string): Date {
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
}

export function fmtDate(value: string | null | undefined): string {
  if (!value) return '';
  try {
    return format(parseLocal(value), 'MMM d');
  } catch {
    return value;
  }
}

export function fmtDateLong(value: string | null | undefined): string {
  if (!value) return '';
  try {
    return format(parseLocal(value), 'MMM d, yyyy');
  } catch {
    return value;
  }
}
